import React, { useState, useMemo, useCallback, useRef } from 'react';
import { isEmpty, noop } from 'lodash';
import classnames from 'classnames';
import pathfinder from '@fiverr-private/pathfinder';
import { ModalType, openFipMigrationModal, shouldDisplayFullMigrationModal } from '@fiverr-private/pro_migration';
import { useSuccessSignCtaEvent, BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { logger } from '@fiverr-private/obs';
import { theme } from '@fiverr-private/theme';
import { ContactSellerButton } from '@fiverr-private/send_message';
import { getContext } from '@fiverr-private/fiverr_context';
import { useEnteredView, useBreakpoints } from '@fiverr-private/hooks';
import { Container } from '@fiverr-private/layout_components';
import {
    LazyComponent,
    contactMeClicked,
    SafeWrapper,
    startMessagingVerificationConfig,
} from '@fiverr-private/seller_pages_toolbox';
import { openCodeActivationModal, useActivationGuard } from '@fiverr-private/code_activation_triggers';
import { useFreelancerPageContext } from '../../../FreelancerPageContext';
import { PAGE_NAME, useMonitoring, mixpanel } from '../../../../monitoring';
import { useSellerVacation } from '../../../FreelancerPage/hooks/useSellerVacation';
import { raiseError, getWindow } from '../../../../utils';
import { useBriefAiAvailablity } from '../useBriefAiAvailability';
import { useFloatingChatTrigger } from '../useFloatingChatTrigger';
import { useRelevantGigs } from '../../../../hooks/useRelevantGigs';
import { handleSignUpModalOpen } from '../../../../utils/getGlobals';
import { isPendingUser } from './utils/isPendingUser';
import { useContactDataClick } from './useContactDataClick';
import { ContactOptions } from './ContactOptions';

import styles from './index.module.scss';
import { ContactButtonDisplayComponent } from './ContactButtonDisplayComponent';

const lazyImportModal = () => import(/* webpackChunkName: 'ContactSellerModal' */ './ContactSellerModal');
const lazyImportDrawer = () => import(/* webpackChunkName: 'ContactSellerDrawer' */ './ContactSellerDrawer');

const ACTIVATION_SOURCE = `${PAGE_NAME}_contact`;

/** @typedef {React.ComponentProps<typeof import('@fiverr-private/button')['Button']>} ButtonProps */
/**
 * @typedef {React.FC<{
 *   className?: string;
 *   username: string;
 *   source: string;
 *   size?: ButtonProps['size'];
 *   aiBriefOnly?: boolean;
 *   buttonCopy?: React.ReactNode;
 *   contactButtonProps?: Partial<ButtonProps>;
 *   renderTrigger?: () => React.ReactNode;
 *   fullWidth?: boolean;
 *   fullHeight?: boolean;
 *   aiBriefInitialGenerationText?: string;
 *   shouldRenderFloatingBtn?: boolean;
 * }>} ContactButtonViewProps
 */

/** @type {ContactButtonViewProps} */
const ContactButtonView = ({
    className,
    username,
    source,
    size,
    renderDropdown = false,
    aiBriefOnly = false,
    buttonCopy,
    contactButtonProps = {},
    renderTrigger,
    fullWidth = true,
    fullHeight,
    aiBriefInitialGenerationText,
    shouldRenderFloatingBtn,
    disableProMigration = false,
}) => {
    const {
        experience: { isBusiness: isBusinessExperience },
    } = getContext();
    const [showModal, setShowModal] = useState(false);
    const buttonRef = useRef(null);
    const { isLoggedIn, sellerId, seller, isCurrentUser, isActivationCompleted, gigs, isOnline } =
        useFreelancerPageContext();
    const mostRelevantGig = useRelevantGigs(gigs)?.[0];
    const { isOnVacation } = useSellerVacation();
    const { md: isMD } = useBreakpoints();
    const isBriefAvailable = useBriefAiAvailablity();
    const { isFloatingChatEnabled, openFloatingChat } = useFloatingChatTrigger();
    const { handleContactButtonImpression } = useMonitoring();
    const { checkShouldActivate } = useActivationGuard(ACTIVATION_SOURCE);

    const profilePhoto = seller?.user?.profileImageUrl;
    const responseTimeInHrs = seller?.responseTime?.inHours;
    const displayName = seller?.user?.profile?.displayName;

    useEnteredView(buttonRef, {
        threshold: 1,
        onEntered: () => {
            if (!shouldRenderDropdown) {
                handleContactButtonImpression('inbox');
            }
        },
    });

    const conversationUrl = pathfinder('conversation', { id: username });
    const isVisible = buttonRef.current ? !!buttonRef.current?.offsetParent : false;
    const userHasPendingState = isPendingUser(isActivationCompleted);
    const {
        isLoading,
        modalInitialData,
        isContactClicked,
        setIsContactClicked,
        setEnableFetchData,
        refetchInitialData,
        isContactClickReady,
    } = useContactDataClick({
        username,
        isVisible,
        isLoggedIn,
        isMD,
        userHasPendingState,
        onError: () => raiseError(),
    });

    const redirectToInbox = useCallback(() => {
        mixpanel.redirectToInbox({ priorInteraction: modalInitialData?.priorInteraction, source });
        getWindow()?.location.assign(conversationUrl);
    }, [conversationUrl, modalInitialData, source]);

    const isLoadingIcon = isContactClicked && isLoading;
    const sellerName = seller?.user?.profile?.displayName || username;
    const emptyGigsList = isEmpty(gigs);

    const sendMixpanelClickContactEvent = (priorInteractions) => {
        mixpanel.clickOnContactMeButton({
            type: shouldRenderDropdown ? 'multi-optional' : 'contact-only',
            source,
            priorInteraction: priorInteractions,
        });
    };

    const isContactDisabled = () => {
        if (isCurrentUser || emptyGigsList) {
            return true;
        } else if (isOnVacation) {
            return !seller?.vacation?.isContactAllowed;
        }
        return false;
    };

    const onContactOpen = useCallback(() => setShowModal(true), []);

    const onContactClick = async () => {
        if (!isMD && typeof window !== 'undefined') {
            window.location.href = conversationUrl;
            return false;
        }

        setIsContactClicked(true);
        contactMeClicked();

        let data = {};
        try {
            data = await isContactClickReady();
        } catch (err) {
            logger.error('Failed to get isContactClickReady data', err);
        }

        const hasPriorInteractions = modalInitialData?.priorInteraction || data?.priorInteraction;

        if (hasPriorInteractions) {
            redirectToInbox();
            return;
        }

        if (!isLoggedIn) {
            return handleSignUpModalOpen({ source, type: BUSINESS_TRIGGER_CTA.CONTACT, data: { sellerName } });
        }

        const { shouldActivate, activationStatus, effective } = await checkShouldActivate();

        if (shouldActivate) {
            await openCodeActivationModal({
                shouldSendActivationCodeOnOpen: true,
                activationStatus: { ...activationStatus, pending_verifications: effective },
                onSuccess: async () => {
                    await refetchInitialData();
                    initiateContact(hasPriorInteractions);
                },
                sourceComponent: ACTIVATION_SOURCE,
                phoneVerificationConfig: startMessagingVerificationConfig,
            });
            return;
        }

        initiateContact(hasPriorInteractions);
    };

    const initiateContact = (hasPriorInteractions) => {
        if (isBusinessExperience && shouldDisplayFullMigrationModal() && !disableProMigration) {
            openFipMigrationModal({
                variant: ModalType.SeamlessUpgradeToPro,
                payload: {
                    source,
                    biEnrichment: {
                        pageName: source,
                        trigger: 'contact',
                    },
                },
            });
        } else if (isFloatingChatEnabled) {
            openFloatingChat();
        } else {
            onContactOpen();
        }
        sendMixpanelClickContactEvent(hasPriorInteractions);
    };

    useSuccessSignCtaEvent({
        source,
        type: BUSINESS_TRIGGER_CTA.CONTACT,
        callback: onContactClick,
    });

    const onMouseEnter = useCallback(() => setEnableFetchData(true), []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, [setShowModal]);

    const modalProps = useMemo(() => {
        const recipientInfo = modalInitialData?.recipient || {};
        const show = showModal && !isEmpty(modalInitialData);
        const displayName = seller?.user?.profile?.displayName;
        const { category_id: categoryId, sub_category_id: subCategoryId } = mostRelevantGig || {};
        return {
            pageName: PAGE_NAME,
            isOpen: show,
            recipientInfo: {
                ...recipientInfo,
                sellerId,
                displayName,
            },
            attachmentsUploadConfig: modalInitialData?.attachmentsUploadConfig,
            showInfo: true,
            source: 'business_page_contact_me',
            onSubmitSuccess: refetchInitialData,
            onClose: handleCloseModal,
            withSatisfactionGuarantee: seller?.isPro && isBusinessExperience,
            ...(isBriefAvailable && categoryId && subCategoryId && { brief: { categoryId, subCategoryId } }),
            ...(seller.vacation && { vacation: seller.vacation }),
        };
    }, [
        refetchInitialData,
        isBriefAvailable,
        mostRelevantGig,
        modalInitialData,
        handleCloseModal,
        seller,
        sellerId,
        showModal,
    ]);

    /** @type {ButtonProps} */
    const buttonProps = isMD
        ? { onClick: onContactClick, onMouseEnter }
        : {
              onClick: () => {
                  sendMixpanelClickContactEvent(modalInitialData?.priorInteraction);
                  redirectToInbox();
              },
          };

    const lazyImportLink = isBusinessExperience ? lazyImportDrawer : lazyImportModal;

    const shouldShowAiBriefButton = useBriefAiAvailablity();
    const shouldRenderDropdown = (renderDropdown && shouldShowAiBriefButton) || aiBriefOnly;

    const contactButtonClass = classnames(styles.contactButton, 'flex-center');

    const displayElement = renderTrigger ? (
        renderTrigger({ onClick: buttonProps.onClick })
    ) : (
        <ContactButtonDisplayComponent
            size={size}
            source={source}
            className={contactButtonClass}
            disabled={isContactDisabled()}
            isLoading={isLoadingIcon}
            buttonCopy={buttonCopy}
            withGradient={isFloatingChatEnabled}
            {...buttonProps}
            {...contactButtonProps}
        />
    );

    return (
        <Container
            width={fullWidth ? '100%' : undefined}
            height={fullHeight ? '100%' : undefined}
            className={className}
            ref={buttonRef}
        >
            {shouldRenderDropdown ? (
                <ContactOptions
                    buttonCopy={buttonCopy}
                    renderTrigger={renderTrigger ? () => renderTrigger({ onClick: noop }) : undefined}
                    onContactClick={onContactClick}
                    disabled={isContactDisabled()}
                    options={{
                        priorInteraction: modalInitialData?.priorInteraction,
                        source,
                        aiBriefInitialGenerationText,
                    }}
                    contactButtonProps={contactButtonProps}
                />
            ) : (
                displayElement
            )}

            <LazyComponent
                moduleName="ContactSellerDrawer"
                lazyImport={lazyImportLink}
                shouldImport={showModal}
                componentProps={modalProps}
            />
            {shouldRenderFloatingBtn && !isContactDisabled() && (
                <Container
                    display={{
                        default: 'none',
                        lg: 'inlineBlock',
                    }}
                    position="fixed"
                    left={theme.spacing['8']}
                    bottom={theme.spacing['8']}
                    zIndex="2"
                >
                    <ContactSellerButton
                        recipientInfo={{
                            username,
                            profilePhoto,
                            responseTimeInHrs,
                            isOnline,
                            displayName,
                        }}
                        onClick={onContactClick}
                        isAssistantActive={isFloatingChatEnabled}
                    />
                </Container>
            )}
        </Container>
    );
};

/** @type {typeof ContactButtonView} */
export const ContactButton = SafeWrapper(ContactButtonView, 'ContactButton');
